import { forwardRef, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, CheckOutline, DuplicateOutline, Flex, IconButton, Image, Text, Tooltip, useToast } from '@bonusly/design-system';

const actionIconStyles = {
	size: 'xs' as const,
	variant: 'ghost' as const,
	colorScheme: 'secondary' as const,
	color: 'info.icon',
	borderRadius: '8px',
	_hover: { bg: 'secondary.bgAlpha', color: 'secondary.fgActive' },
	_active: { bg: 'secondary.bgAlphaHover', color: 'secondary.fgActive' },
};

function formatMessageTime(raw: Date | string): string {
	const date = raw instanceof Date ? raw : new Date(raw);
	const now = new Date();
	const isToday = date.toDateString() === now.toDateString();
	if (isToday) return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
	return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
}

function UserMessageActions({ content, timestamp }: { content: string; timestamp?: Date | string }) {
	const { t } = useTranslation('admin_bizy');
	const toast = useToast();
	const [copied, setCopied] = useState(false);
	const timeoutRef = useRef<ReturnType<typeof setTimeout>>();

	useEffect(() => {
		return () => {
			if (timeoutRef.current) clearTimeout(timeoutRef.current);
		};
	}, []);

	const handleCopy = useCallback(() => {
		navigator.clipboard
			.writeText(content)
			.then(() => {
				setCopied(true);
				if (timeoutRef.current) clearTimeout(timeoutRef.current);
				timeoutRef.current = setTimeout(() => setCopied(false), 2000);
			})
			.catch(() => {
				toast({ title: t('feedback.copyFailed'), status: 'error', duration: 2000 });
			});
	}, [content, toast, t]);

	return (
		<Flex align="center" gap="4px" justifyContent="flex-end">
			{timestamp && (
				<Text fontSize="xs" color="text.softest" mr={1}>
					{formatMessageTime(timestamp)}
				</Text>
			)}
			<Tooltip label={copied ? t('feedback.copied') : t('feedback.copy')} placement="top">
				<IconButton
					aria-label={t('feedback.copy')}
					icon={copied ? <CheckOutline boxSize={5} /> : <DuplicateOutline boxSize={5} />}
					{...actionIconStyles}
					onClick={handleCopy}
				/>
			</Tooltip>
		</Flex>
	);
}

export interface BizyUserMessageProps {
	content: string;
	attachmentPreviewUrl?: string;
	timestamp?: Date | string;
	isFresh?: boolean;
	fontSize?: string | object;
	showActions?: boolean;
}

export const BizyUserMessage = forwardRef<HTMLDivElement, BizyUserMessageProps>(function BizyUserMessage(
	{ content, attachmentPreviewUrl, timestamp, isFresh = false, fontSize, showActions = true },
	ref
) {
	return (
		<Flex ref={ref} direction="column" alignItems="flex-end" alignSelf="flex-end" maxW="85%" role="group" data-user-message="true">
			<Box
				bg="secondary.bgAlpha"
				borderRadius={12}
				borderBottomRightRadius={4}
				px={3}
				py={2}
				boxShadow="0 1px 1px 0 rgba(0, 0, 0, 0.12)"
				w="fit-content"
				maxW="100%"
				transformOrigin="bottom right"
				sx={
					isFresh
						? {
								'@keyframes userMessagePop': {
									'0%': { opacity: 0, transform: 'translateY(18px) scale(0.82)' },
									'60%': { opacity: 1, transform: 'translateY(-3px) scale(1.04)' },
									'100%': { opacity: 1, transform: 'translateY(0) scale(1)' },
								},
								animation: 'userMessagePop 0.42s cubic-bezier(0.34, 1.56, 0.64, 1)',
							}
						: undefined
				}
			>
				<Text fontSize={fontSize} whiteSpace="pre-wrap" overflowWrap="anywhere">
					{content}
				</Text>
				{attachmentPreviewUrl && <Image src={attachmentPreviewUrl} alt={content} maxH="120px" borderRadius="md" objectFit="cover" mt={2} />}
			</Box>
			{showActions && (
				<Box opacity={0} _groupHover={{ opacity: 1 }} transition="opacity 0.15s ease-in-out" mt={1}>
					<UserMessageActions content={content} timestamp={timestamp} />
				</Box>
			)}
		</Flex>
	);
});
